/**
 * Sync state machine - tagged union for all possible sync states
 */

// Sync state as a discriminated union
export type SyncState =
  | Idle
  | Fetching
  | Applying
  | Complete
  | SyncError
  | Conflict;

export interface Idle {
  readonly _tag: 'Idle';
  readonly lastSyncAt: string | null;
}

export const Idle = (lastSyncAt: string | null = null): Idle => ({ _tag: 'Idle', lastSyncAt });

export interface Fetching {
  readonly _tag: 'Fetching';
  readonly startedAt: string;
  readonly cursor: string | null;
  readonly pagesFetched: number;
  readonly recordsFetched: number;
}

export const Fetching = (
  startedAt: string,
  cursor: string | null = null,
  pagesFetched: number = 0,
  recordsFetched: number = 0
): Fetching => ({ _tag: 'Fetching', startedAt, cursor, pagesFetched, recordsFetched });

export interface Applying {
  readonly _tag: 'Applying';
  readonly recordsToApply: number;
  readonly recordsApplied: number;
}

export const Applying = (recordsToApply: number, recordsApplied: number = 0): Applying => ({
  _tag: 'Applying',
  recordsToApply,
  recordsApplied,
});

export interface Complete {
  readonly _tag: 'Complete';
  readonly completedAt: string;
  readonly recordsSynced: number;
  readonly duration: number; // milliseconds
}

export const Complete = (completedAt: string, recordsSynced: number, duration: number): Complete => ({
  _tag: 'Complete',
  completedAt,
  recordsSynced,
  duration,
});

export interface SyncError {
  readonly _tag: 'SyncError';
  readonly error: Error;
  readonly failedAt: string;
  readonly partialProgress: {
    pagesFetched: number;
    recordsSynced: number;
  } | null;
}

export const SyncError = (
  error: Error,
  failedAt: string,
  partialProgress: { pagesFetched: number; recordsSynced: number } | null = null
): SyncError => ({ _tag: 'SyncError', error, failedAt, partialProgress });

export interface Conflict {
  readonly _tag: 'Conflict';
  readonly conflicts: readonly ConflictRecord[];
  readonly detectedAt: string;
}

export const Conflict = (conflicts: readonly ConflictRecord[], detectedAt: string): Conflict => ({
  _tag: 'Conflict',
  conflicts,
  detectedAt,
});

// Conflict representation
export interface ConflictRecord {
  readonly recordId: string;
  readonly recordType: 'expense' | 'settlement' | 'pool_event' | 'category' | 'group';
  readonly local: unknown;
  readonly remote: unknown;
  readonly lastModifiedLocal: string;
  readonly lastModifiedRemote: string;
}

// Sync metadata persisted in IndexedDB
export interface SyncMetadata {
  readonly groupId: string;
  readonly state: SyncState;
  readonly lastFullSyncAt: string | null;
  readonly lastIncrementalSyncAt: string | null;
  readonly cursor: string | null;
  readonly generation: number; // Incremented on each sync
}

// Helper functions for state transitions
export function canTransitionTo(from: SyncState, to: SyncState['_tag']): boolean {
  switch (from._tag) {
    case 'Idle':
      return to === 'Fetching';
    case 'Fetching':
      return to === 'Applying' || to === 'SyncError' || to === 'Complete';
    case 'Applying':
      return to === 'Complete' || to === 'SyncError' || to === 'Conflict';
    case 'Complete':
      return to === 'Idle' || to === 'Fetching';
    case 'SyncError':
      return to === 'Idle' || to === 'Fetching';
    case 'Conflict':
      return to === 'Idle' || to === 'Fetching' || to === 'Applying';
  }
}
